/**
 * Raeumliche Darstellung der Lage des Roboters (W12).
 *
 * Ergaenzung zum Diagramm, kein Ersatz: eine Kurve zeigt, **wann** sich etwas
 * geaendert hat, dieser Koerper zeigt, **wie** der Roboter gerade steht. Beim
 * Einstellen eines Lagefilters braucht man beides - die Kurve fuer den Verlauf,
 * die Ansicht fuer die Frage, ob das Ergebnis ueberhaupt plausibel ist.
 *
 * Gezeigt wird immer der jüngste Messpunkt. Eine Bewegung nachzuspielen waere
 * eine eigene Aufgabe; hier geht es um den Vergleich mit dem Roboter, der
 * daneben steht.
 *
 * Die Rechnung dahinter steht in orientation.ts und ist dort geprueft.
 */

import { Text } from "@microbit/ui";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, HStack, VStack } from "styled-system/jsx";
import {
  latestQuaternion,
  projectedAxes,
  projectedFaces,
  rotationMatrix,
  shapeRadius,
  viewBasis,
} from "./orientation";
import type { TelemetrySeries } from "./series";

interface OrientationViewProps {
  series: TelemetrySeries;
  height: number;
}

/** Kantenlaenge der Zeichenflaeche in eigenen Koordinaten. */
const SIZE = 200;

/** Wieviel Rand um die aeusserste Ecke bleibt. */
const MARGIN = 0.12;

const OrientationView = ({ series, height }: OrientationViewProps) => {
  const intl = useIntl();
  const q = latestQuaternion(series);

  if (!q) {
    return (
      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        bg="white"
        height={`${height}px`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p="3"
      >
        <Text fontSize="xs" color="fg.muted" textAlign="center">
          <FormattedMessage id="telemetry-orientation-empty" />
        </Text>
      </Box>
    );
  }

  const matrix = rotationMatrix(q);
  const basis = viewBasis();
  // Der Maszstab haengt an der Form, nicht an der gerade sichtbaren Ansicht:
  // sonst wuechse und schrumpfte der Roboter beim Drehen.
  const scale = ((SIZE / 2) * (1 - MARGIN)) / shapeRadius();
  const faces = projectedFaces(matrix, basis);
  const axes = projectedAxes(matrix, basis);

  return (
    <VStack alignItems="stretch" gap="1">
      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        bg="white"
      >
        <svg
          viewBox={`0 0 ${SIZE} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label={intl.formatMessage({ id: "telemetry-orientation-alt" })}
        >
          <g transform={`translate(${SIZE / 2} ${height / 2}) scale(${scale})`}>
            {/*
              Ein waagerechter Kreis als Bezug. Ohne ihn ist bei einer reinen
              Drehung um die Senkrechte nicht zu sehen, was oben ist.
            */}
            <ellipse
              cx={0}
              cy={0}
              rx={1.0}
              ry={1.0 * Math.sin((22 * Math.PI) / 180)}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth={1 / scale}
            />
            {faces.map((face) => (
              <polygon
                key={face.name}
                points={face.points}
                fill={face.fill}
                stroke={face.stroke}
                strokeWidth={1 / scale}
                strokeLinejoin="round"
              />
            ))}
            {/* Achsen zuletzt, damit sie auch vor dem Koerper sichtbar sind. */}
            {axes.map((axis) => (
              <line
                key={axis.label}
                x1={axis.x1}
                y1={axis.y1}
                x2={axis.x2}
                y2={axis.y2}
                stroke={axis.colour}
                strokeWidth={2 / scale}
                strokeLinecap="round"
                opacity={0.85}
              />
            ))}
          </g>
        </svg>
      </Box>
      {/*
        Die vier Zahlen darunter: die Ansicht zeigt eine Drehung, aber nicht,
        welche Zahlen dazu gehoeren. Beim Vergleich mit einer Rechnung von Hand
        braucht man sie.
      */}
      <HStack gap="2" flexWrap="wrap" data-testid="orientation-values">
        {(
          [
            ["w", q.w],
            ["x", q.x],
            ["y", q.y],
            ["z", q.z],
          ] as const
        ).map(([name, wert]) => (
          <Text key={name} fontSize="2xs" color="fg.muted">
            {name} {wert.toFixed(3)}
          </Text>
        ))}
      </HStack>
    </VStack>
  );
};

export default OrientationView;
